import { PropsWithChildren, ReactElement } from 'react';
import { Link, usePage } from '@inertiajs/react';
import { ApplicationLogo } from '@/Components/Layout';
import { LanguageSelector } from '@/Components/System';
import { useTranslations } from '@/hooks/use-translations';
import { PageProps } from '@/types';
import { GridLines } from '@/Layouts/AppGuestLayout/GridLines';
import { H1, H2 } from '@/Components/DataDisplay';

interface GuestLayoutProps extends PropsWithChildren {
    title?: string;
    subtitle?: string;
}

export default function GuestLayout({ children, title, subtitle }: GuestLayoutProps): ReactElement {
    const { t } = useTranslations();
    const { tenant } = usePage<PageProps>().props;

    const displayName = tenant?.name ?? 'Novazelle';
    const logoUrl = tenant?.logo_url ?? null;

    return (
        <div className="flex min-h-screen">
            {/* Left panel — brand */}
            <div className="relative hidden w-[52%] flex-col justify-between overflow-hidden bg-brand-surface p-14 lg:flex">
                <GridLines />

                {/* Accent line top */}
                <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-brand-accent via-brand-accent-hover to-transparent" />

                {/* Logo area */}
                <div className="relative z-10">
                    {/* Company logo and name */}
                    <div className="text-2xl font-bold tracking-tight text-white uppercase mb-4">
                        Nova<span className="text-brand-accent">zelle</span>
                        <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-accent">
                            {t('brand.tagline')}
                        </p>
                    </div>
                    <Link href="/" className="flex items-center gap-3">
                        <span className="text-2xl font-bold tracking-tight text-brand-accent uppercase">
                            {displayName}
                        </span>
                    </Link>
                </div>

                {/* Center content */}
                <div className="relative z-10 flex flex-col gap-8">
                    <div>
                        <H1 size="lg" className="text-white">
                            {t('brand.headline_primary')}
                            <br />
                            <span className="text-brand-accent">
                                {t('brand.headline_secondary')}
                            </span>
                        </H1>
                        <p className="mt-4 max-w-sm text-base leading-relaxed text-brand-muted">
                            {t('brand.platform_description')}
                        </p>
                    </div>

                    {/* Stats strip */}
                    <div className="flex gap-8 border-t border-white/10 pt-8">
                        {[
                            { value: '100%', label: t('brand.stat_uptime') },
                            { value: '4+', label: t('brand.stat_languages') },
                            { value: '11+', label: t('brand.stat_markets') },
                        ].map((stat) => (
                            <div key={stat.label}>
                                <p className="text-xl font-bold text-white">
                                    {stat.value}
                                </p>
                                <p className="mt-0.5 text-xs text-brand-muted-dim">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <p className="relative z-10 text-xs text-brand-muted-dimmer">
                    &copy; {new Date().getFullYear()} NOVAZELLE.{' '}
                    {t('common.all_rights_reserved')}
                </p>
            </div>

            {/* Right panel — form */}
            <div className="relative flex flex-1 flex-col items-center justify-center bg-brand-form-surface px-6 py-12">
                {/* Language selector */}
                <div className="absolute right-4 top-4">
                    <LanguageSelector />
                </div>

                {/* Mobile logo */}
                <div className="mb-10 lg:hidden">
                    <Link href="/">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt={displayName}
                                className="h-8 w-auto"
                            />
                        ) : (
                            <ApplicationLogo />
                        )}
                    </Link>
                </div>

                <div className="w-full max-w-sm">
                    {(title || subtitle) && (
                        <div className="mb-8">
                            {title && (
                                <H2 size="lg">
                                    {title}
                                </H2>
                            )}
                            {subtitle && (
                                <p className="mt-1.5 text-sm text-muted-foreground">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    )}

                    {children}
                </div>
            </div>
        </div>
    );
}
